import React from "react";
import { motion } from "framer-motion";

const PURPLE_GRADIENT = "linear-gradient(2deg, #A28FDC 0.74%, #7C3AED 74.61%)";

const EvDailyCard = ({ car, onBook, winWidth = 1280 }) => {
  const imgW = winWidth >= 1880 ? 176 : winWidth >= 1536 ? 251 : winWidth >= 1280 ? 230 : 210;
  const imgH = Math.round(imgW * (135 / 251));

  const price = car.rentPerHr != null ? Number(car.rentPerHr) : null;

  return (
    <motion.article
      layout
      className={`relative bg-white border border-[#E5E7EB] rounded-xl shadow-[0px_8px_26px_-11px_rgba(212,184,184,0.07)] flex p-4 overflow-hidden transition-all min-w-0 gap-3 ${car.isSoldOut ? "opacity-60" : ""}`}
    >
      {/* Left: details */}
      <div className="flex flex-col justify-between flex-1 min-w-0">

        {/* Top section */}
        <div className="flex flex-col gap-4">

          {/* Name */}
          <span className="text-[#252525] text-[22px] font-semibold font-['Outfit'] leading-[1.2] tracking-[0.4px] text-left block">
            {car.name}
          </span>

          {/* Badges + Specs */}
          <div className="flex flex-col gap-[7px]">
            {/* Row 1: Electric + Type badge */}
            <div className="flex items-center gap-2">
              <div className="bg-[rgba(88,255,88,0.36)] flex gap-[6px] min-[1880px]:gap-[4px] h-[24px] min-[1880px]:h-[17px] items-center px-[8px] min-[1880px]:px-[5.6px] rounded-[6px] flex-1 min-w-0">
                <img alt="" src="/assets/icons/ev/leaf.svg" className="w-[13px] h-[13px] min-[1880px]:w-[9px] min-[1880px]:h-[9px] shrink-0" />
                <span className="text-[#005a00] text-[11px] min-[1880px]:text-[7.7px] font-medium font-['Outfit'] leading-none whitespace-nowrap">Electric</span>
              </div>
              {car.bodyTypeText && (
                <div className="bg-[#f9fafb] border border-[#f3f3f9] flex items-center justify-center px-[8px] min-[1880px]:px-[5.6px] py-[2px] h-[24px] min-[1880px]:h-[17px] rounded-[6px] shrink-0 w-[66px] min-[1880px]:w-[46px]">
                  <span className="text-[#1d4ed8] text-[12px] min-[1880px]:text-[8.4px] font-semibold font-['Outfit'] tracking-[1px] uppercase whitespace-nowrap">
                    {car.bodyTypeText}
                  </span>
                </div>
              )}
            </div>

            {/* Row 2: Seats + Transmission */}
            <div className="flex gap-2">
              <div className="bg-[#f7f9fd] border border-[#edf0f7] flex flex-1 gap-[9px] min-[1880px]:gap-[6px] items-center px-[8px] min-[1880px]:px-[5.6px] py-[4px] min-[1880px]:py-[3px] rounded-[8px] min-w-0">
                <img alt="" src="/assets/icons/ev/users-round.svg" className="w-[16px] h-[16px] min-[1880px]:w-[11px] min-[1880px]:h-[11px] shrink-0" />
                <span className="text-[#161e2c] text-[10px] min-[1880px]:text-[7px] font-medium font-['Outfit'] leading-[12px] min-[1880px]:leading-[8.4px] truncate">
                  {car.seater} Seats
                </span>
              </div>
              <div className="bg-[#f7f9fd] border border-[#edf0f7] flex flex-1 gap-[9px] min-[1880px]:gap-[6px] items-center px-[8px] min-[1880px]:px-[5.6px] py-[4px] min-[1880px]:py-[3px] rounded-[8px] min-w-0">
                <img alt="" src="/assets/icons/ev/transmission.svg" className="w-[16px] h-[16px] min-[1880px]:w-[11px] min-[1880px]:h-[11px] shrink-0" />
                <span className="text-[#161e2c] text-[10px] min-[1880px]:text-[7px] font-medium font-['Outfit'] leading-[12px] min-[1880px]:leading-[8.4px] truncate">
                  {car.transmissionTypeText}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: Pricing */}
        <div className="flex flex-col gap-3 mt-4">
          <div className="flex flex-col gap-[3px]">
            {/* Price */}
            {price != null ? (
              <div className="flex items-end gap-0 leading-none text-[#cd2525]">
                <span className="text-[29px] font-bold font-['Outfit'] tracking-[-0.3px]">₹{price.toLocaleString("en-IN")}/</span>
                <span className="text-[20px] font-medium font-['Outfit'] tracking-[0.6px] mb-[2px]">hr</span>
              </div>
            ) : (
              <span className="text-[#9CA3AF] text-[16px] font-medium font-['Outfit']">Price unavailable</span>
            )}
            {/* Incl. taxes line */}
            <span className="text-[#a2a2a2] text-[14px] font-bold font-['Outfit'] leading-[20px] tracking-[0.6px] text-left block">
              {car.baseFare != null && car.taxAmount != null && (
                <span className="text-[#a2a2a2]">₹{(Number(car.baseFare) + Number(car.taxAmount)).toLocaleString("en-IN")} · </span>
              )}
              Incl. Taxes &amp; Excl. Fees
            </span>
          </div>

          {/* Extra km line */}
          {car.extraKmRental != null && car.totalFreeKms != null && (
            <p className="text-[#727272] text-[14px] font-medium font-['Outfit'] tracking-[0.14px] leading-none text-left">
              ₹{car.extraKmRental}/km after{" "}
              <span className="text-[#1d4ed8] font-bold">{car.totalFreeKms} km</span>
              {" "}limit
            </p>
          )}
        </div>
      </div>

      {/* Right: image + driving range + Book Now */}
      <div className="flex flex-col items-start justify-between shrink-0" style={{ width: imgW }}>
        {/* Car image */}
        <div style={{ width: imgW, height: imgH }} className="mt-5">
          <img
            src={car.imageUrl || "/assets/sample.png"}
            alt={car.name}
            className="w-full h-full object-contain"
          />
        </div>

        {/* Driving range + Book Now */}
        <div className="flex flex-col gap-[9px] w-full mt-2">
          {car.drivingRange && (
            <div className="flex gap-2 items-center justify-center">
              <img alt="" src="/assets/icons/ev/route.svg" className="w-[19px] h-[19px] shrink-0" />
              <div className="flex flex-col">
                <span className="text-[#161e2c] text-[10.7px] font-medium font-['Outfit'] leading-[12.9px]">
                  {car.drivingRange} km
                </span>
                <span className="text-[#7e8ba5] text-[8.6px] font-normal font-['Outfit'] leading-[10.7px]">
                  Driving Range (per charge)
                </span>
              </div>
            </div>
          )}
          <button
            disabled={car.isSoldOut}
            onClick={() => onBook(car)}
            className={`w-full h-[36px] px-[17px] py-[8px] rounded-[8px] text-[16px] font-medium font-['Outfit'] tracking-[0.47px] text-white border whitespace-nowrap ${car.isSoldOut ? "bg-gray-200 text-gray-400 border-gray-200" : "border-[#d5d5d5] shadow-[0px_11.6px_14px_rgba(124,58,237,0.2)]"}`}
            style={!car.isSoldOut ? { backgroundImage: PURPLE_GRADIENT } : undefined}
          >
            {car.isSoldOut ? "Sold Out" : "Book Now"}
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default EvDailyCard;
